const ZONE_MAP = {
  Eastern: 'America/New_York',
  Central: 'America/Chicago',
  Mountain: 'America/Denver',
  Pacific: 'America/Los_Angeles',
  Alaska: 'America/Anchorage',
  Hawaii: 'Pacific/Honolulu',
};

export function getIanaZone(timeZone, isDst) {
  // Arizona-style: Mountain time without DST observance.
  if (timeZone === 'Mountain' && !isDst) return 'America/Phoenix';
  return ZONE_MAP[timeZone] || null;
}

/**
 * Returns the school-local Y/M/D + H/M/S that correspond to `now` (a Date
 * representing absolute time). Falls back to the browser's local fields when
 * the time zone is unknown.
 */
export function getSchoolLocalParts(school, now = new Date()) {
  const zone = getIanaZone(school?.time_zone, school?.is_dst);
  if (!zone) {
    return {
      year: now.getFullYear(),
      month: now.getMonth() + 1,
      day: now.getDate(),
      hour: now.getHours(),
      minute: now.getMinutes(),
      second: now.getSeconds(),
      zone: null,
    };
  }

  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: zone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });

  const parts = Object.fromEntries(formatter.formatToParts(now).map((part) => [part.type, part.value]));
  // Intl can return "24" for hour at midnight on some engines; normalize.
  const hour = parts.hour === '24' ? 0 : Number(parts.hour);

  return {
    year: Number(parts.year),
    month: Number(parts.month),
    day: Number(parts.day),
    hour,
    minute: Number(parts.minute),
    second: Number(parts.second),
    zone,
  };
}

function parseTimeOfDay(value) {
  const match = String(value || '').match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?/);
  if (!match) return null;
  return {
    hour: Number(match[1]),
    minute: Number(match[2]),
    second: Number(match[3] || 0),
  };
}

/**
 * Seconds remaining today (in the school's time zone) until `targetTimeStr`.
 * Returns a negative number if the time has already passed school-locally.
 * Returns null when either input is missing/invalid.
 */
export function secondsUntilSchoolTime(school, targetTimeStr, now = new Date()) {
  const target = parseTimeOfDay(targetTimeStr);
  if (!target) return null;

  const local = getSchoolLocalParts(school, now);
  const currentSeconds = local.hour * 3600 + local.minute * 60 + local.second;
  const targetSeconds = target.hour * 3600 + target.minute * 60 + target.second;
  return targetSeconds - currentSeconds;
}
